import { useEffect, type MutableRefObject } from 'react';
import type { AndroidUpdateOffer } from '../core/config/androidUpdate';
import { canInstallApk } from '../adapters/mobile/apkInstaller';

/** Foreground resume: retry install after permission settings, else refresh offer. */
export function useAndroidUpdateResume(input: {
  enabled: boolean;
  pendingPermissionRetryRef: MutableRefObject<boolean>;
  offerRef: MutableRefObject<AndroidUpdateOffer | null>;
  refresh: () => Promise<unknown>;
  update: (offer?: AndroidUpdateOffer | null) => Promise<boolean>;
}) {
  const {
    enabled, pendingPermissionRetryRef, offerRef, refresh, update,
  } = input;

  useEffect(() => {
    if (!enabled) return undefined;
    void refresh();
    let handle: { remove: () => Promise<void> } | undefined;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) return;
          void (async () => {
            if (pendingPermissionRetryRef.current) {
              if ((await canInstallApk()) && offerRef.current) {
                pendingPermissionRetryRef.current = false;
                await update(offerRef.current);
                return;
              }
            }
            await refresh();
          })();
        });
      } catch { /* web */ }
    })();
    return () => { void handle?.remove(); };
  }, [enabled, refresh, update, pendingPermissionRetryRef, offerRef]);
}
